import * as React from 'react';
import { FC, useEffect, useMemo, useState } from 'react';
import { connect } from "react-redux";
import { ChangeOptions } from 'react-redux-form';
import { Modal } from 'src/components/common';
import { TextFormInput } from 'src/components/form';
import { FontAwesomeIcon, FontAwesomeIcons } from 'src/constants/fontAwesomeIcons';
import { ModalSize } from 'src/enumerations/ModalSize';
import * as ProjectActions from '../../../actions/project';
import * as Dtos from '../../../dtos/Tmd.dtos';
import * as AuthenticationHelper from '../../../helpers/authentication';
import { ITmdState } from "../../../reducers/index";
import { debounce } from "lodash";
import { RequestState } from 'src/enumerations/RequestState';

import './ProjectPage';

interface ProjectConvertConfirmationModelProps {
    permissions?: Dtos.PersonnelTmdPermissions[];
    project: Dtos.Project;
    validatingProject: boolean;
    saveProject: ProjectActions.IProjectSaveActionCreator;
    validateProject: ProjectActions.IProjectValidateActionCreator;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    validationFailures: Dtos.ResponseError[];
}

const ProjectConvertConfirmationModel: FC<ProjectConvertConfirmationModelProps> = ({
    permissions,
    project,
    validatingProject,
    saveProject,
    validateProject,
    isOpen,
    setIsOpen,
    validationFailures
}) => {
    //
    // -- States --
    const [studyAcronym, setStudyAcronym] = useState<string>("");
    const [protocolId, setProtocolId] = useState<string>("");
    const [scientificTitle, setScientificTitle] = useState<string>("");
    const [shortTitle, setShortTitle] = useState<string>("");
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const onCancel = () => {
        setIsOpen(false);
        setProtocolId("");
        setScientificTitle("");
        setShortTitle("");
        setStudyAcronym("");
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            if ((protocolId != "" || scientificTitle != "") && hasBeenSubmitted && !validatingProject) {
                await validateProject(
                    Object.assign(
                        { ...project }, 
                        {
                            projectOutcomeId : 2,
                            protocolID: protocolId,
                            studyAcronym: studyAcronym,
                            title: scientificTitle,
                            shortTitle: shortTitle
                        }
                    )
                );
            }
        }, 250);
    
        debounceValidate();
    
        return () => {
            debounceValidate.cancel();
        };
    }, [
        protocolId,
        scientificTitle,
        validateProject,
        validatingProject,
        hasBeenSubmitted
    ]);

    const renderProjectConvertForm = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.ProjectCreate,
            undefined,
            undefined
        );
        
        if (hasPermission) {
            var protocolIdFailure = validationFailures?.find(x => x.fieldName == "ProtocolID");
            var titleFailure = validationFailures?.find(x => x.fieldName == "Title");

            return (
                <Modal
                    open={isOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        onCancel();
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <h2 className="modal-title">Convert Project to Trial</h2>
                        <button type="button" className="close" aria-label="Close" onClick={() => { onCancel() }}>
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="form-row py-2 px-4">
                            <div className="col-2">
                                <span style={{ color: protocolIdFailure ? "#dc3545" : "black" }}>Protocol ID</span>
                            </div>
                            <div className="col-10">
                                <TextFormInput 
                                    mapProps={null}
                                    id={"ProtocolID"} 
                                    name={"ProtocolID"} 
                                    value={protocolId} 
                                    onChange={(value: any, _options?: ChangeOptions) => {
                                        setProtocolId(value);
                                        return null;
                                    }}
                                    invalid={ protocolIdFailure ? true : false}
                                    showFeedbackIcon={false}
                                />
                                {protocolIdFailure && (
                                    <span className="py-1" style={{ color: "#dc3545" }}>{protocolIdFailure.message}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-2">
                                <span style={{ color: titleFailure ? "#dc3545" : "black" }}>Scientific Title</span>
                            </div>
                            <div className="col-10">
                                <TextFormInput 
                                    mapProps={null}
                                    id={"ScientificTitle"} 
                                    name={"ScientificTitle"} 
                                    value={scientificTitle} 
                                    onChange={(value: any, _options?: ChangeOptions) => {
                                        setScientificTitle(value);
                                        return null;
                                    }}
                                    invalid={ titleFailure ? true : false}
                                    showFeedbackIcon={false}
                                />
                                {titleFailure && (
                                    <span className="py-1" style={{ color: "#dc3545" }}>{titleFailure.message}</span>
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-2">
                                <span>Acronym</span>
                            </div>
                            <div className="col-10">
                                <TextFormInput 
                                    mapProps={null}
                                    id={"studyAcronym"} 
                                    name={"studyAcronym"} 
                                    value={studyAcronym} 
                                    onChange={(value: any, _options?: ChangeOptions) => {
                                        setStudyAcronym(value);
                                        return null;
                                    }}
                                />
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-2">
                                <span>Acronym Source</span>
                            </div>
                            <div className="col-10">
                                <TextFormInput 
                                    mapProps={null}
                                    id={"ShortTitle"} 
                                    name={"ShortTitle"} 
                                    value={shortTitle} 
                                    onChange={(value: any, _options?: ChangeOptions) => {
                                        setShortTitle(value);
                                        return null;
                                    }}
                                />
                            </div>
                        </div>
                        <div className="form-row py-2 px-4 d-flex justify-content-end">
                            <button 
                                disabled={protocolId == "" || studyAcronym == "" || scientificTitle == "" || shortTitle == ""}
                                onClick={ async () => {
                                    if (project) {

                                        if (!hasBeenSubmitted) {
                                            setHasBeenSubmitted(true);
                                        }

                                        await saveProject(
                                            Object.assign(
                                                { ...project }, 
                                                {
                                                    projectOutcomeId : 2,
                                                    protocolID: protocolId,
                                                    studyAcronym: studyAcronym,
                                                    title: scientificTitle,
                                                    shortTitle: shortTitle
                                                }
                                            )
                                        );
                                    }
                                }}
                                type="button"
                                className="btn btn-primary mr-2"
                                style={{
                                    cursor: (protocolId == "" || studyAcronym == "" || scientificTitle == "" || shortTitle == "") ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Convert
                            </button>
                            <button
                                onClick={() => { onCancel(); }}
                                type="button"
                                className="btn btn-outline-primary mr-2"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        isOpen, 
        permissions, 
        onCancel,
        protocolId,
        studyAcronym,
        scientificTitle,
        shortTitle,
        validationFailures,
        hasBeenSubmitted,
        setHasBeenSubmitted,
        saveProject
    ]);

    return (
        <>
            { renderProjectConvertForm }
        </>
    );
}

const mapStateToProps = (state: ITmdState, ownProps: Partial<ProjectConvertConfirmationModelProps>): Partial<ProjectConvertConfirmationModelProps> => {

    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];

    return {
        project: ownProps.project,
        validatingProject: state.projects.validateState && state.projects.validateState.status == RequestState.Pending,
        permissions: permissions,
        isOpen: ownProps.isOpen,
        validationFailures: ownProps.project ?
            state.projects.validationFailures :
            undefined,
    };
};

const mapDispatchToProps = (_dispatch, ownProps: Partial<ProjectConvertConfirmationModelProps>): Partial<ProjectConvertConfirmationModelProps> => {
    return {
        saveProject: ownProps.saveProject,
        validateProject: ownProps.validateProject
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectConvertConfirmationModel);
